import * as React from 'react';
import { Radio, Col, Button } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { RadioChangeEvent } from 'antd/lib/radio';
import { NativeButtonProps } from 'antd/lib/button/button';
import styled from 'styled-components';

import { notArchivedFilterParam } from 'config/config';
import { SubmitButton } from 'common/components';
import {
    addReferenceSuffixToBenfitOutcome,
    FormFieldIds,
    getStatusLabel,
} from 'common/helpers/utils';
import { FilterData } from 'common/helpers/url';
import { showNotification, notificationText } from 'common/helpers/notifications';
import { Profile } from 'features/user/models/userModel';
import { AutocompleteSelect } from 'features/pagination/components/AutocompleteSelect';
import { AutocompleteField, PaginationView } from 'features/pagination/models/paginationModel';
import { PaginatedTable } from 'features/pagination/components/PaginatedTable';
import { BenefitData } from 'features/download/components/DownloadBenefits';
import { getBenefitsSuccess, getBenefitsWithSubmittersSuccess } from 'features/benefit/actions';
import { AssignActions, EditBenefitPaths } from 'features/benefit/models/benefitModels';
import { Antd3Form, Antd3FormProps, StyledAntd3Form } from 'common/components/deprecated/antd3';
import { StatusPill } from 'common/components/StatusPill';
import { FilterDrawerBenefits } from 'common/components/FilterDrawerBenefits';

import { SupplierEditContractsContainerProps } from '../containers/SupplierEditContractsContainer';
import { SupplierAssignPage } from './SupplierAssignPage';
import { SupplierAssignPageTitles, SupplierAssignPaths } from '../models/editModel';
import { debounce } from 'lodash';

const FiltersWrapper = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 24px;
`;

const StyledButton = styled((props: NativeButtonProps) => <Button {...props} />)`
    && {
        height: 40px;
        border-radius: 999px !important;
        padding-left: 18px !important;
        padding-right: 18px !important;
    }
`;

const SearchWrapper = styled.div`
    flex: 1;
    height: 40px;
`;

interface SupplierEditContractsState {
    editingUserProfile?: Profile;
    showInviteButton: boolean;
    showSaveButton: boolean;
    assign: AssignActions;
    selectedBenefitIds: string[];
    isFilterDrawerOpen: boolean;
}

type SupplierEditContractsProps = Antd3FormProps & SupplierEditContractsContainerProps;

const SupplierEditContractsColumns = [
    {
        title: 'Benefit',
        key: 'outcome',
        render: ({ outcome }: BenefitData) => <span>{outcome}</span>,
    },
    {
        title: 'Benefit Label',
        key: 'label',
        render: ({ label }: BenefitData) => label,
    },
    {
        title: 'Quantity',
        key: 'quantity',
        render: ({ quantity }: BenefitData) => <span>{quantity}</span>,
    },
    {
        title: 'Submitters',
        key: 'submitters',
        render: ({ submitters }: BenefitData) => submitters,
    },
    {
        title: 'Status',
        key: 'status',
        render: ({ status }: BenefitData) => <StatusPill status={status} />,
    },
    {
        title: 'Completion Date',
        key: 'finalDeliveryDate',
        render: ({ finalDeliveryDate }: BenefitData) => finalDeliveryDate,
    },
    {
        title: 'Project',
        key: 'contractTitle',
        render: ({ contractTitle, contractReferenceNumber }: BenefitData) =>
            `${contractTitle} | ${contractReferenceNumber} `,
    },
];

class SupplierEditContractsForm extends React.Component<
    SupplierEditContractsProps,
    SupplierEditContractsState
> {
    public state: SupplierEditContractsState = {
        showInviteButton: true,
        showSaveButton: false,
        assign: AssignActions.Assign,
        selectedBenefitIds: [],
        isFilterDrawerOpen: false,
    };

    private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.props.form.validateFields((error, { userIds }) => {
            if (error) {
                return showNotification({ text: notificationText.Error });
            }
            return this.props.toggleMultipleEmployeesRoleRequest({
                benefitIds: this.state.selectedBenefitIds.map((id) => parseInt(id, 10)),
                userIds: userIds.map((id: string) => parseInt(id, 10)),
                action: this.state.assign,
            });
        });
        debounce(this.refreshTable, 300)();
    };

    private refreshTable = (filters?: FilterData) =>
        this.props.paginationRequest({
            view: PaginationView.SupplierEditContract,
            pagination: {
                current: 1,
            },
            filters,
            paginationSuccessCallback: getBenefitsWithSubmittersSuccess,
        });

    private saveFilters = (contractId: number | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.SupplierEditContract,
            values: {
                contract: contractId,
            },
        });

    private searchBenefits = (contractId: number) =>
        this.saveFilters(contractId) && this.refreshTable();

    private onRemoveContractAutocomplete = () => this.saveFilters(undefined) && this.refreshTable();

    private onSelectChange = (selectedBenefitIds: string[]) =>
        this.setState({ selectedBenefitIds });

    private handleAssignChange = (event: RadioChangeEvent) =>
        this.setState({ assign: event.target.value });

    private clearFilters = () =>
        this.props.clearFilters(PaginationView.SupplierEditContract) &&
        this.refreshTable() &&
        this.props.form.setFieldsValue({ contractId: '' });

    public render(): JSX.Element {
        const { form, filter } = this.props;
        const isBenefitUnselected = !this.state.selectedBenefitIds.length;
        const userIds = form.getFieldValue('userIds');
        const isEmployeeUnselected = !(userIds && userIds.length);
        return (
            <StyledAntd3Form onSubmit={this.handleSubmit} header="Assign Employees" bigHeader>
                <SupplierAssignPage tile={SupplierAssignPageTitles.Assign} />
                <FiltersWrapper>
                    <StyledButton
                        type="primary"
                        icon={<FilterOutlined />}
                        onClick={() => this.setState({ isFilterDrawerOpen: true })}
                    >
                        Filter
                    </StyledButton>
                    <FilterDrawerBenefits
                        visible={this.state.isFilterDrawerOpen}
                        onClose={() => this.setState({ isFilterDrawerOpen: false })}
                        filterId={PaginationView.SupplierEditContract}
                        onApply={this.refreshTable}
                    />
                    <SearchWrapper>
                        <AutocompleteSelect
                            id={FormFieldIds.ContractId}
                            placeholder="Search contracts"
                            form={form}
                            autocompleteField={
                                AutocompleteField.ExecutiveAdminSearchContractsAssignBenefit
                            }
                            onSelect={this.searchBenefits}
                            onRemove={this.onRemoveContractAutocomplete}
                            notFoundContent="No contracts found"
                            requiredMessage="Contract is required"
                            showLabel={false}
                            defaultValue={filter && filter.values.contract}
                        />
                    </SearchWrapper>
                    <StyledButton onClick={this.clearFilters}>Clear Filter</StyledButton>
                </FiltersWrapper>
                <PaginatedTable
                    data={addReferenceSuffixToBenfitOutcome(this.props.benefits)}
                    columns={SupplierEditContractsColumns}
                    emptyText="No benefits found"
                    view={PaginationView.SupplierEditContract}
                    paginationSuccessCallback={getBenefitsSuccess}
                    rowSelection={{
                        selectedRowKeys: this.state.selectedBenefitIds,
                        onChange: this.onSelectChange,
                    }}
                    pageNumber={this.props.pageNumber}
                    pathUrl={SupplierAssignPaths.Contracts}
                    withPaginationHistory
                />
                <AutocompleteSelect
                    label="Select employees"
                    id="userIds"
                    form={form}
                    autocompleteField={AutocompleteField.Employee}
                    notFoundContent="No employees found"
                    multiple
                    required
                    requiredMessage="Please choose at least one employee"
                    searchParams={notArchivedFilterParam}
                />
                <Antd3Form.Item>
                    <Radio.Group
                        name="assign"
                        defaultValue={AssignActions.Assign}
                        onChange={this.handleAssignChange}
                    >
                        <Radio value={AssignActions.Assign}>Assign</Radio>
                        <Radio value={AssignActions.Revoke}>Unassign</Radio>
                    </Radio.Group>
                </Antd3Form.Item>
                <SubmitButton
                    value={this.state.assign === AssignActions.Assign ? 'Assign' : 'Unassign'}
                    disabled={isBenefitUnselected || isEmployeeUnselected}
                />
            </StyledAntd3Form>
        );
    }
}

export const SupplierEditContracts = Antd3Form.create({})(SupplierEditContractsForm);
